// @ts-strict-ignore
import { isEmpty } from 'lodash';
import { API } from '@shared/types/api';
import { Action } from '@shared/util/context';
import { WorkflowTemplateFormData, WorkflowTemplateUpdateData } from '../hooks';

type WT = API.WorkflowTemplateFragment;

export interface WorkflowTemplateState {
  workflowTemplate: WorkflowTemplateFormData | WorkflowTemplateUpdateData;
}

export type WorkflowTemplateActions =
  | Action<'setWorkflowTemplate', { args: WT }>
  | Action<'addComponent', { args: WT['components'][number] }>
  | Action<'updateComponent', { args: WT['components'][number] }>
  | Action<'reorderComponent', { args: { source: number; target: number } }>
  | Action<'archiveComponent', { args: string }>
  | Action<'setName', { args: string }>
  | Action<'reset'>;

export function reducer(
  state: WorkflowTemplateState,
  action: WorkflowTemplateActions,
): WorkflowTemplateState {
  switch (action.type) {
    case 'setWorkflowTemplate': {
      const workflowTemplate = action.args;
      return {
        workflowTemplate,
      };
    }
    case 'addComponent': {
      const component = action.args;
      const newComponents = [...state.workflowTemplate.components, component];
      return {
        ...state,
        workflowTemplate: {
          ...state.workflowTemplate,
          components: newComponents,
        },
      };
    }
    case 'archiveComponent': {
      const id = action.args;
      const updatedComponents = state.workflowTemplate.components.filter(
        component => component.id !== id,
      );
      const archivedComponent = state.workflowTemplate.components.find(
        component => component.id === id,
      );
      const archivedComponents = isEmpty(state.workflowTemplate.archivedComponents)
        ? [archivedComponent]
        : state.workflowTemplate.archivedComponents.concat(archivedComponent);
      return {
        ...state,
        workflowTemplate: {
          ...state.workflowTemplate,
          components: updatedComponents,
          archivedComponents,
        },
      };
    }
    case 'updateComponent': {
      const component = action.args;
      const newComponents = state.workflowTemplate.components.map(c => {
        if (c.id === component.id) {
          return component;
        }
        return c;
      });
      return {
        ...state,
        workflowTemplate: {
          ...state.workflowTemplate,
          components: newComponents,
        },
      };
    }
    case 'reorderComponent': {
      const { source, target } = action.args;
      const reorderedComponents = moveComponent(state.workflowTemplate.components, source, target);
      return {
        ...state,
        workflowTemplate: {
          ...state.workflowTemplate,
          components: reorderedComponents,
        },
      };
    }
    case 'reset': {
      return { workflowTemplate: null };
    }
    case 'setName': {
      return {
        ...state,
        workflowTemplate: {
          ...state.workflowTemplate,
          name: action.args,
        },
      };
    }
    default: {
      return state;
    }
  }
}

function moveComponent(
  components: API.AnyComponent[],
  currentIndex: number,
  newIndex: number,
): API.AnyComponent[] {
  const reorderedComponents = Array.from(components);
  const [component] = reorderedComponents.splice(currentIndex, 1);
  reorderedComponents.splice(newIndex, 0, component);
  return reorderedComponents;
}
